import React from 'react';
import { router } from '@inertiajs/react';

export type Column<T> = {
  key: string;
  header: string;
  /** 例 '120px'。不给的话这一列吃掉剩余宽度。 */
  width?: string;
  align?: 'right';
  mono?: boolean;
  /** 允许这一列省略号截断。不给的话内容一律不换行、放不下就溢出。 */
  truncate?: boolean;
  render: (row: T) => React.ReactNode;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  rowClassName?: (row: T) => string;
  empty?: string;
  pagination?: { page: number; per: number; total: number };
};

// 翻页保留当前的全部查询参数（搜索词、筛选），只换 page。
// 后台的页面要能把地址直接发给别人，所以分页走 URL 而不是组件 state。
function goToPage(page: number) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', String(page));
  router.get(url.pathname + url.search, {}, { preserveState: false, preserveScroll: true });
}

function pageNumbers(current: number, last: number): (number | '…')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(last - 1, current + 1);
  if (from > 2) out.push('…');
  for (let i = from; i <= to; i += 1) out.push(i);
  if (to < last - 1) out.push('…');
  out.push(last);
  return out;
}

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
  empty = '没有匹配的记录',
  pagination,
}: Props<T>) {
  const last = pagination ? Math.max(1, Math.ceil(pagination.total / pagination.per)) : 1;
  const from = pagination ? (pagination.page - 1) * pagination.per + 1 : 0;
  const to = pagination ? Math.min(pagination.page * pagination.per, pagination.total) : 0;

  return (
    <div className="overflow-hidden rounded-lg border border-admin-line bg-admin-surface">
      {/* 表本身可横向滚动，页面不横滚 —— 列多时溢出的是表，不是布局 */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={c.width ? { width: c.width } : undefined}
                  className={`whitespace-nowrap border-b border-admin-line bg-admin-surface2 px-2.5 py-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-admin-muted ${
                    c.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-2.5 py-8 text-center text-[13px] text-admin-muted"
                >
                  {empty}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={rowKey(row)} className={`group ${rowClassName?.(row) ?? ''}`}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[
                      // 任何一格都不换行：一格换行整行高度翻倍，密集表的
                      // 扫读节奏就断了。放不下的列自己开 truncate。
                      'h-[34px] whitespace-nowrap border-b border-admin-line px-2.5 align-middle',
                      'group-last:border-b-0 group-hover:bg-admin-surface2',
                      c.align === 'right' ? 'text-right tabular-nums' : '',
                      c.mono ? 'font-mono text-[11.5px] text-admin-ink2' : 'text-[13px]',
                      c.truncate ? 'max-w-px overflow-hidden text-ellipsis' : '',
                    ].join(' ')}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total > 0 && (
        <div className="flex items-center gap-1.5 border-t border-admin-line bg-admin-surface2 px-2.5 py-2">
          <span className="text-[12px] tabular-nums text-admin-muted">
            显示 {from}–{to}，共 {pagination.total}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => goToPage(pagination.page - 1)}
            className="h-6 min-w-[26px] rounded-md border border-admin-line2 bg-admin-surface text-[12px] disabled:opacity-40"
          >
            ←
          </button>
          {pageNumbers(pagination.page, last).map((n, i) =>
            n === '…' ? (
              <span key={`gap-${i}`} className="text-admin-muted">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                onClick={() => goToPage(n)}
                className={`h-6 min-w-[26px] rounded-md border text-[12px] ${
                  n === pagination.page
                    ? 'border-admin-accent bg-admin-accent font-semibold text-white'
                    : 'border-admin-line2 bg-admin-surface'
                }`}
              >
                {n}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={pagination.page >= last}
            onClick={() => goToPage(pagination.page + 1)}
            className="h-6 min-w-[26px] rounded-md border border-admin-line2 bg-admin-surface text-[12px] disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
